import type { Component } from 'solid-js';
import { onCleanup, onMount } from 'solid-js';
import { fullscreenSupported, isApiFullscreen, isInstalledPwa, isSmallPhone } from '../utils/device';

const DISMISS_KEY = 'clepta:fs-prompt';
const NEVER_KEY = 'clepta:fs-prompt-never';

/**
 * One-time (per browsing session) hint attached to the navbar fullscreen icon.
 * The icon's vanilla runtime handler performs the actual fullscreen request.
 */
export const FullscreenPrompt: Component = () => {
    onMount(() => {
        // Installed PWAs already launch chrome-less; phones aren't worth it.
        if (isInstalledPwa() || isSmallPhone() || !fullscreenSupported() || isApiFullscreen()) return;
        try {
            // localStorage: dismissed forever. sessionStorage: this visit only.
            if (localStorage.getItem(NEVER_KEY)) return;
            if (sessionStorage.getItem(DISMISS_KEY)) return;
        } catch {
            /* storage blocked -- still fine to show the hint */
        }

        const button = document.querySelector<HTMLButtonElement>('.js-fullscreen-toggle');
        if (!button) return;

        let hideTimer: number | undefined;
        let originalTitle: string | null = null;
        const tooltip = document.createElement('span');
        tooltip.id = 'fullscreen-tooltip';
        tooltip.className = 'fullscreen-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.innerHTML =
            '<span class="fullscreen-tooltip__title">Bekijk foto\'s op hun best</span>' +
            '<span class="fullscreen-tooltip__text">Klik hier voor volledig scherm</span>';

        const dismiss = () => {
            if (!tooltip.isConnected) return;
            tooltip.classList.add('fullscreen-tooltip--leaving');
            button.classList.remove('fullscreen-hint-active');
            try {
                sessionStorage.setItem(DISMISS_KEY, '1');
            } catch {
                /* ignore */
            }
            window.setTimeout(() => tooltip.remove(), 180);
            button.removeAttribute('aria-describedby');
            if (originalTitle !== null) button.setAttribute('title', originalTitle);
        };

        // Let the header settle before pointing out the control.
        const showTimer = window.setTimeout(() => {
            if (button.style.display === 'none' || isApiFullscreen()) return;
            originalTitle = button.getAttribute('title');
            button.removeAttribute('title');
            button.setAttribute('aria-describedby', tooltip.id);
            button.append(tooltip);
            button.classList.add('fullscreen-hint-active');
            requestAnimationFrame(() => tooltip.classList.add('fullscreen-tooltip--visible'));
            hideTimer = window.setTimeout(dismiss, 8000);
        }, 800);

        button.addEventListener('click', dismiss, { once: true });
        onCleanup(() => {
            window.clearTimeout(showTimer);
            if (hideTimer !== undefined) window.clearTimeout(hideTimer);
            button.removeEventListener('click', dismiss);
            button.classList.remove('fullscreen-hint-active');
            tooltip.remove();
            button.removeAttribute('aria-describedby');
            if (originalTitle !== null) button.setAttribute('title', originalTitle);
        });
    });

    return null;
};
